/**
 * 归因看板 — 归因事件审计 + 漏斗概览
 *
 * 由两个区域组成:
 *  1. 概览卡片 (status 计数 + ack 率 + event_kind 计数) — 一次 GET /summary
 *  2. 事件表格 (游标分页, 可过滤 status/event_kind/click_id/日期) — GET /events
 *     点击表格行展开一个 Drawer 显示该事件的 ad_event_logs (回传请求/响应审计)
 *
 * 注: ROAS 待接巨量广告花费 (cost API)，本期不做，页面底部有说明。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Card,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Statistic,
  Table,
  Tag,
  Button,
  DatePicker,
  Space,
  Typography,
  Alert,
  Descriptions,
  Timeline,
  message,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import {
  listAttributionEvents,
  getAttributionSummary,
  getAttributionEventDetail,
  type AttributionEvent,
  type AttributionEventStatus,
  type AttributionSummary,
  type AdEventLog,
  type EventDetailResult,
} from '@/api/attribution';
import { formatDateTime, truncate } from '@/utils/format';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// ---------------------------------------------------------------------------
// Status display helpers
// ---------------------------------------------------------------------------

const statusColor: Record<AttributionEventStatus, string> = {
  pending: 'gold',
  sent: 'blue',
  acked: 'green',
  failed: 'red',
  skipped: 'default',
};

const statusIcon: Record<AttributionEventStatus, React.ReactNode> = {
  pending: <ClockCircleOutlined />,
  sent: <ClockCircleOutlined />,
  acked: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  skipped: <MinusCircleOutlined />,
};

const statusLabel: Record<AttributionEventStatus, string> = {
  pending: '待发送',
  sent: '已发送',
  acked: '已确认',
  failed: '失败',
  skipped: '跳过',
};

const eventKindOptions = [
  { value: 'register', label: '注册' },
  { value: 'key_action', label: '关键行为' },
  { value: 'first_pay', label: '首次付款' },
  { value: 'repeat_pay', label: '重复付款' },
  { value: 'share', label: '分享' },
  { value: 'view_content', label: '浏览内容' },
  { value: 'add_to_wishlist', label: '添加愿望清单' },
];

const statusOptions: Array<{ value: AttributionEventStatus; label: string }> = [
  { value: 'pending', label: '待发送' },
  { value: 'sent', label: '已发送' },
  { value: 'acked', label: '已确认' },
  { value: 'failed', label: '失败' },
  { value: 'skipped', label: '跳过' },
];

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------

function SummaryCards({ summary, loading }: { summary: AttributionSummary | null; loading: boolean }) {
  const sc: Record<string, number> = summary?.status_counts ?? {};
  const ackRate = summary?.ack_rate_pct ?? 0;

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Spin spinning={loading}>
            <Statistic
              title="已确认 (acked)"
              value={sc['acked'] ?? 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Spin>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Spin spinning={loading}>
            <Statistic
              title="失败 (failed)"
              value={sc['failed'] ?? 0}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<CloseCircleOutlined />}
            />
          </Spin>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Spin spinning={loading}>
            <Statistic
              title="待处理 (pending)"
              value={sc['pending'] ?? 0}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ClockCircleOutlined />}
            />
          </Spin>
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card size="small">
          <Spin spinning={loading}>
            <Statistic
              title="Ack 率"
              value={ackRate.toFixed(1)}
              suffix="%"
              valueStyle={{ color: ackRate >= 80 ? '#52c41a' : ackRate >= 50 ? '#fa8c16' : '#ff4d4f' }}
            />
          </Spin>
        </Card>
      </Col>
    </Row>
  );
}

// ---------------------------------------------------------------------------
// Event logs drawer
// ---------------------------------------------------------------------------

function EventLogsDrawer({
  open,
  eventId,
  onClose,
}: {
  open: boolean;
  eventId: number | null;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<EventDetailResult | null>(null);

  useEffect(() => {
    if (!open || eventId === null) return;
    setLoading(true);
    setDetail(null);
    getAttributionEventDetail(eventId)
      .then(setDetail)
      .catch(() => message.error('加载回传日志失败'))
      .finally(() => setLoading(false));
  }, [open, eventId]);

  const ev = detail?.event;
  const logs: AdEventLog[] = detail?.logs ?? [];

  return (
    <Drawer
      title={ev ? `事件 #${ev.id} — ${ev.event_kind}` : '回传审计'}
      placement="right"
      width={640}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {ev && (
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 24 }}>
            <Descriptions.Item label="状态" span={1}>
              <Tag color={statusColor[ev.status]}>{statusLabel[ev.status]}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="重试次数" span={1}>
              {ev.attempts}
            </Descriptions.Item>
            <Descriptions.Item label="click_id" span={2}>
              <Text copyable={!!ev.click_id}>{ev.click_id || '—'}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="用户 ID" span={2}>
              {ev.user_id || '—'}
            </Descriptions.Item>
            {ev.last_error && (
              <Descriptions.Item label="最后错误" span={2}>
                <Text type="danger">{ev.last_error}</Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="发送时间" span={1}>
              {ev.sent_at ? formatDateTime(ev.sent_at) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="确认时间" span={1}>
              {ev.acked_at ? formatDateTime(ev.acked_at) : '—'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {logs.length === 0 && !loading && (
          <Text type="secondary">暂无回传日志</Text>
        )}

        {logs.length > 0 && (
          <Timeline
            items={logs.map((log) => ({
              color: log.http_status && log.http_status < 300 ? 'green' : 'red',
              children: (
                <div key={log.id}>
                  <div style={{ marginBottom: 4 }}>
                    <Tag color={log.direction === 'outbound' ? 'blue' : 'purple'}>
                      {log.direction}
                    </Tag>
                    {log.http_status && (
                      <Tag color={log.http_status < 300 ? 'green' : 'red'}>
                        HTTP {log.http_status}
                      </Tag>
                    )}
                    {log.duration_ms != null && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {' '}
                        {log.duration_ms}ms
                      </Text>
                    )}
                    <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                      {formatDateTime(log.created_at)}
                    </Text>
                  </div>
                  {log.error_msg && (
                    <Alert
                      type="error"
                      message={log.error_msg}
                      style={{ marginBottom: 4 }}
                      banner
                    />
                  )}
                  {log.raw_response && (
                    <pre
                      style={{
                        background: '#f5f5f5',
                        padding: 8,
                        borderRadius: 4,
                        fontSize: 11,
                        maxHeight: 120,
                        overflow: 'auto',
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(log.raw_response, null, 2)}
                    </pre>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Spin>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

interface FilterState {
  status?: AttributionEventStatus;
  event_kind?: string;
  click_id?: string;
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

export function AttributionEventsPage() {
  const [form] = Form.useForm<FilterState>();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState<AttributionSummary | null>(null);

  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState<AttributionEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string>('');
  const [cursors, setCursors] = useState<string[]>([]); // cursor stack for "prev" support

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);

  const filtersRef = useRef<FilterState>({});

  // --- load summary ---
  const loadSummary = useCallback(() => {
    setSummaryLoading(true);
    getAttributionSummary({ trend_days: 7 })
      .then(setSummary)
      .catch(() => message.error('加载概览统计失败'))
      .finally(() => setSummaryLoading(false));
  }, []);

  // --- load events ---
  const loadEvents = useCallback((cursor?: string) => {
    const f = filtersRef.current;
    const params: Record<string, unknown> = { limit: 20 };
    if (f.status) params.status = f.status;
    if (f.event_kind) params.event_kind = f.event_kind;
    if (f.click_id) params.click_id = f.click_id;
    if (f.dateRange?.[0]) params.date_from = f.dateRange[0].format('YYYY-MM-DD');
    if (f.dateRange?.[1]) params.date_to = f.dateRange[1].add(1, 'day').format('YYYY-MM-DD');
    if (cursor) params.cursor = cursor;

    setEventsLoading(true);
    listAttributionEvents(params as never)
      .then((res) => {
        setEvents(res.data ?? []);
        setNextCursor(res.next_cursor ?? '');
      })
      .catch(() => message.error('加载事件列表失败'))
      .finally(() => setEventsLoading(false));
  }, []);

  useEffect(() => {
    loadSummary();
    loadEvents();
  }, [loadSummary, loadEvents]);

  const onSearch = () => {
    const values = form.getFieldsValue();
    filtersRef.current = values;
    setCursors([]);
    loadEvents();
  };

  const onReset = () => {
    form.resetFields();
    filtersRef.current = {};
    setCursors([]);
    loadEvents();
  };

  const onNextPage = () => {
    if (!nextCursor) return;
    setCursors((prev) => [...prev, nextCursor]);
    loadEvents(nextCursor);
  };

  const onPrevPage = () => {
    const prev = [...cursors];
    prev.pop(); // remove current
    const prevCursor = prev[prev.length - 1] || '';
    setCursors(prev);
    loadEvents(prevCursor || undefined);
  };

  const onRowClick = (ev: AttributionEvent) => {
    setSelectedEventId(ev.id);
    setDrawerOpen(true);
  };

  // --- table columns ---
  const columns: ColumnsType<AttributionEvent> = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 160,
      render: (v) => formatDateTime(v),
    },
    {
      title: '事件类型',
      dataIndex: 'event_kind',
      width: 130,
      render: (v) => <Tag color="geekblue">{v}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: AttributionEventStatus) => (
        <Tag color={statusColor[v]} icon={statusIcon[v]}>
          {statusLabel[v]}
        </Tag>
      ),
    },
    {
      title: 'click_id',
      dataIndex: 'click_id',
      width: 160,
      render: (v) => <Text copyable={!!v}>{v ? truncate(v, 18) : '—'}</Text>,
    },
    {
      title: '用户 ID',
      dataIndex: 'user_id',
      width: 130,
      render: (v) => (v ? truncate(v, 14) : '—'),
    },
    {
      title: 'App ID',
      dataIndex: 'app_id',
      width: 80,
      render: (v) => <Tag color="blue">#{v}</Tag>,
    },
    {
      title: '重试次数',
      dataIndex: 'attempts',
      width: 80,
      align: 'center',
    },
    {
      title: '失败原因',
      dataIndex: 'last_error',
      ellipsis: true,
      render: (v) =>
        v ? (
          <Text type="danger" ellipsis={{ tooltip: v }}>
            {v}
          </Text>
        ) : (
          '—'
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, row) => (
        <Button size="small" type="link" onClick={() => onRowClick(row)}>
          回传日志
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* Summary cards */}
      <SummaryCards summary={summary} loading={summaryLoading} />

      {/* Kind counts */}
      {summary && Object.keys(summary.kind_counts ?? {}).length > 0 && (
        <Card
          size="small"
          title="事件类型分布"
          style={{ marginBottom: 16 }}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              累计
            </Text>
          }
        >
          <Space wrap>
            {Object.entries(summary.kind_counts).map(([kind, count]) => (
              <Tag key={kind} color="geekblue">
                {kind}: {count}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* Filter bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={onSearch}>
          <Form.Item name="status" label="状态">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 120 }}
              options={statusOptions.map((o) => ({ value: o.value, label: o.label }))}
            />
          </Form.Item>
          <Form.Item name="event_kind" label="事件类型">
            <Select
              allowClear
              placeholder="全部"
              style={{ width: 140 }}
              options={eventKindOptions}
            />
          </Form.Item>
          <Form.Item name="click_id" label="click_id">
            <Input allowClear placeholder="投放平台 click_id" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="dateRange" label="日期范围">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} htmlType="submit">
                查询
              </Button>
              <Button onClick={onReset}>重置</Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  loadSummary();
                  onSearch();
                }}
              >
                刷新
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Events table */}
      <Table<AttributionEvent>
        dataSource={events}
        columns={columns}
        rowKey="id"
        loading={eventsLoading}
        pagination={false}
        size="small"
        scroll={{ x: 1100 }}
        onRow={(row) => ({
          style: { cursor: 'pointer' },
          onClick: () => onRowClick(row),
        })}
      />

      {/* Cursor pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
        <Button disabled={cursors.length === 0} onClick={onPrevPage}>
          上一页
        </Button>
        <Button disabled={!nextCursor} onClick={onNextPage}>
          下一页
        </Button>
      </div>

      {/* ROAS disclaimer */}
      <Alert
        type="info"
        showIcon
        style={{ marginTop: 24 }}
        message="ROAS 暂不可用"
        description="广告花费数据 (cost API) 需对接巨量引擎 /report/cost 接口，本期未接入。看板聚焦「回传健康度 (ack 率)」和「事件漏斗计数」。"
      />

      {/* Event logs drawer */}
      <EventLogsDrawer
        open={drawerOpen}
        eventId={selectedEventId}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
